import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionSummary, Alert,
  Box,
  Button, Dialog,
  DialogContent, Divider,
  Grid,
  Radio,
  Typography
} from '@mui/material'
import React, { useState } from 'react'
import {
  useCreateCharityDisbursementOptionsQuery, useGetCharityDisbursementsQuery,
  useGetCharityOptionsQuery
} from '../../../../api/disbursements/disbursementApi'
import { type AssetHolderUpdateDto } from '../../../../api/schema-dtos/AssetHolderCrudDtos'
import {
  type CreateCharityDisbursementPayload
} from '../../../../api/schema-dtos/AssetHolderDisbursementPayloadDtos'
import type { LedgerAccountDetails } from '../../../../api/schema-dtos/AssetHolderFinancePayloadDtos'
import { RtkQueryHelper } from '../../../../api/shared/RtkQueryHelper'
import { useAppDispatch } from '../../../../app/hooks'
import OtpComponent from '../../../auth/components/OtpComponent'
import ErrorComponent from '../../../shared/components/ui-widgets/ErrorComponent'
import LoadingComponent from '../../../shared/components/ui-widgets/LoadingComponent'
import { setNotification } from '../../../shared/components/ui-widgets/uiWidgetsSlice'
import type { AssetHolderIdProps } from '../../pages/DashboardHome'

interface ComponentProps extends AssetHolderIdProps {
  assetHolder: AssetHolderUpdateDto
  wallets: LedgerAccountDetails[]
  maxCardWidth: number
}

const AccordionCharityDisbursementOptions: React.FC<ComponentProps> = ({ assetHolderId, assetHolder, wallets, maxCardWidth }: ComponentProps) => {
  const dispatch = useAppDispatch()

  const [expanded, setExpanded] = useState<boolean>(false)
  const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false)
  const [selectedCharityId, setSelectedCharityId] = useState<number>(0)
  const charityOptionsApiResult = useGetCharityOptionsQuery(assetHolderId)
  const charityDisbursementsApiResult = useGetCharityDisbursementsQuery(assetHolderId)
  const [apiCreateInstruction, { isLoading }] = useCreateCharityDisbursementOptionsQuery()

  const requireKyc = !assetHolder.hasPassedKyc && assetHolder.kycRequiredForDonations

  const filteredWallets: LedgerAccountDetails[] = wallets.filter(w => w.availableBalance > 0)
  const pendingWithdrawals = wallets.filter(w => w.pendingWithdrawalsBalance > 0)
  const pendingConversions = wallets.filter(w => w.pendingConversionsBalance > 0)
  const donationsEnabled = !requireKyc && filteredWallets.length > 0 && pendingWithdrawals.length === 0 && pendingConversions.length === 0

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const selectedCharityOrganisationId = parseInt(event.target.value)
    setSelectedCharityId(selectedCharityOrganisationId)
  }

  const handleCreateInstruction = (otp: string): void => {
    const body: CreateCharityDisbursementPayload = {
      assetHolderId,
      selectedCharityOrganisationId: selectedCharityId,
      otp
    }
    apiCreateInstruction({ assetHolderId, body })
      .unwrap()
      .then(() => {
        dispatch(setNotification({
          open: true,
          severity: 'success',
          message: 'Your balances have been successfully donated to the charity\'s wallets.'
        }))
        setOpenConfirmDialog(false)
        setSelectedCharityId(0)
        setExpanded(false)
      })
      .catch((err) => {
        dispatch(setNotification({ open: true, severity: 'error', message: RtkQueryHelper.mapResponseError(err) }))
      })
  }

  const accordionIdentifier = `dashboard-charity-disbursement-${assetHolderId}`

  const getSelectedCharityName = (): string | undefined => {
    return charityOptionsApiResult.data?.find((c) => c.organisationId === selectedCharityId)?.organisationName
  }

  return (
    <>
      <Accordion sx={{ px: 4, pt: 2 }} expanded={expanded} onChange={(evt, expanded) => {
        setExpanded(expanded)
      }}>

        <AccordionSummary expandIcon={<ExpandMoreIcon color={'primary'}/>}
                          aria-controls={accordionIdentifier}
                          id={accordionIdentifier} sx={{ px: 0, pb: 2 }}>
          {/* <Typography variant={'body1'}>Donate all your balances to a selected charity</Typography> */}
          <Typography variant={'h3'} sx={{ mb: 1 }}>Charity Contributions</Typography>

        </AccordionSummary>

        <Box sx={{ pb: 4 }}>

          {!donationsEnabled && requireKyc &&
            <Alert severity="warning" sx={{ px: 2, mb: 4, maxWidth: maxCardWidth }}>
              <b>Note:</b> Please complete your KYC on the &apos;My Profile&apos; tab in order to donate your
              wallet balances to charity.
            </Alert>
          }

          {!donationsEnabled && filteredWallets.length === 0 && (
            <Box>
              <Alert severity="info" sx={{ px: 2, mb: 4 }}>
                Charity contributions can not be created as all wallets have a zero balance.
              </Alert>
            </Box>
          )}

          {!donationsEnabled && pendingWithdrawals.length > 0 && (
            <Box>
              <Alert severity="info" sx={{ px: 2, mb: 4 }}>
                Charity contributions can not be created as you have pending Cash Outs. Please cancel the Cash outs
                before using the charity donation option.
              </Alert>
            </Box>
          )}

          {!donationsEnabled && pendingConversions.length > 0 && (
            <Box>
              <Alert severity="info" sx={{ px: 2, mb: 4 }}>
                Charity contributions can not be created as you have pending BTC to ZAR conversion instructions.
                Please cancel the instruction before using the charity donation option.
              </Alert>
            </Box>
          )}

          {donationsEnabled && (

            <Grid container>
              <Grid item md={12}>
                <Typography variant={'body1'}>
                  If you would like to donate all your balances to a charity then please select a charity
                  below and then click the donate button. This will immediately transfer all balances to the
                  charity&apos;s wallets.
                </Typography>
              </Grid>

              <Grid item md={12}>
                <Typography variant={'body1'} sx={{ mt: 1, mb: 1 }}>Please select a charity</Typography>
              </Grid>

              {charityOptionsApiResult.isLoading && <LoadingComponent/>}
              {charityOptionsApiResult.isError &&
                <ErrorComponent
                  message={RtkQueryHelper.mapResponseError(charityOptionsApiResult.error)}/>}

              {(charityOptionsApiResult.isSuccess && charityOptionsApiResult.data.map((charity) =>
                <React.Fragment key={charity.organisationId}>
                  <Grid item md={6} xs={12}>
                    <Box display={'flex'}>
                      <Radio sx={{ py: 0, my: 0 }}
                             name={'selectedCharityOrganisationId'}
                             value={charity.organisationId}
                             checked={(selectedCharityId === charity.organisationId)}
                             onChange={handleChange}/>

                      <Typography variant={'body1'} display={'inline'} flexGrow={'1'}>
                        <span>{charity.organisationName}</span>
                      </Typography>

                    </Box>
                  </Grid>

                  <Grid item md={6} xs={12} sx={{ pb: { xs: 2, md: 1 }, pl: { xs: 6, md: 0 } }}>
                    <Typography variant={'body1'} display={'inline'}>
                    <span>
                      <a href={charity.websiteUrl} target={'_blank'} rel="noreferrer">
                        {charity.websiteUrl}
                      </a>
                    </span>
                    </Typography>

                  </Grid>
                </React.Fragment>
              ))}

              <Grid item md={12}>&nbsp;</Grid>

              <Grid item md={12} sx={{ display: 'flex' }} justifyContent={'space-between'}>

                <Button
                  variant={'contained'}
                  type="button"
                  sx={{ ml: 2, mr: 2 }}
                  disabled={selectedCharityId === 0}
                  onClick={() => {
                    setOpenConfirmDialog(true)
                  }}
                >
                  Donate
                </Button>

                {/* <OtpComponent */}
                {/*  assetHolderId={assetHolderId} */}
                {/*  actionName={'Donate'} */}
                {/*  isLoading={isLoading} */}
                {/*  authenticatorConfigured={null} */}
                {/*  executeAction={(otp: string) => { */}
                {/*    handleCreateInstruction(otp) */}
                {/*  }} */}
                {/*  disabled={selectedCharityId === 0} */}
                {/* /> */}

                <Button
                  variant={'outlined'}
                  type="button"
                  sx={{ ml: 0 }}
                  onClick={() => {
                    setExpanded(false)
                    setSelectedCharityId(0)
                  }}
                >
                  Cancel
                </Button>

              </Grid>

            </Grid>
          )}

          {charityDisbursementsApiResult.isLoading && <LoadingComponent/>}
          {charityDisbursementsApiResult.isError &&
            <ErrorComponent
              message={RtkQueryHelper.mapResponseError(charityDisbursementsApiResult.error)}/>}

          {charityDisbursementsApiResult.isSuccess && charityDisbursementsApiResult.data.length > 0 && (
            <>
              <Divider sx={{ mt: 4, mb: 4 }}/>
              <Typography variant={'h4'}>Previous charity donations</Typography>
              <Grid container spacing={0} sx={{ pb: 4 }}>

                {charityDisbursementsApiResult.data.map((ci) => (
                  <React.Fragment key={ci.charityDisbursementId}>
                    <Grid item xs={4} sx={{ mr: 0 }}>
                      <Typography variant={'body1'}>{ci.selectedCharityName}</Typography>
                    </Grid>
                    <Grid item xs={3} sx={{ mr: 0 }}>
                      <Typography variant={'body1'}>{ci.currencyCode}&nbsp;{ci.amountFormatted}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant={'body1'}>{ci.processedAtFormatted}</Typography>
                    </Grid>
                    <Grid item xs={1}/>
                  </React.Fragment>

                ))}
              </Grid>
            </>
          )}

        </Box>
      </Accordion>

      <Dialog
        open={openConfirmDialog}
        maxWidth={'sm'}
        fullWidth={true}
        disableEscapeKeyDown={true}
        onClose={() => {
          setOpenConfirmDialog(false)
        }}
        sx={{ p: 2 }}
      >
        {/* <DialogTitle>Donate </DialogTitle> */}

        <DialogContent>
          <Typography variant={'body1'}>You are about to donate the balances of all your wallets to
            the {getSelectedCharityName()} charity. Are you sure you wish to continue? </Typography>
          <Box display={'flex'} justifyContent={'space-between'} sx={{ mt: 2 }}>
            <OtpComponent
              assetHolderId={assetHolderId}
              actionName={'Continue'}
              authenticatorConfigured={null}
              isLoading={isLoading}
              executeAction={(otp: string) => {
                handleCreateInstruction(otp)
              }}
              disabled={false}
            />

            <Button
              variant={'outlined'}
              type="button"
              sx={{ ml: 2 }}
              onClick={() => {
                setOpenConfirmDialog(false)
              }}
            >
              Cancel
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

    </>
  )
}

export default AccordionCharityDisbursementOptions
